import { useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchNotifications,
  fetchUnreadCount,
  markNotificationRead,
  markAllNotificationsRead,
} from "../api/notifications";
import { groupNotifications } from "../utils/notificationGroups";

// The viewer's own IANA zone — what "today" and "yesterday" mean to them.
const viewerZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

// Unread count is always mounted (drives the bell's red dot) and polls in
// the background, same shape as ClockedInBanner's ["myOpenClock"] query — a
// schedule change can arrive from someone else's action at any time.
// The full list is lazy: only fetched once the panel is actually opened
// (same `enabled: open` convention FilterViews/PMO Tools use), since most
// page loads never open it.
export function useNotifications({ open = false } = {}) {
  const queryClient = useQueryClient();

  const { data: unreadCount = 0 } = useQuery({
    queryKey: ["notifications", "unreadCount"],
    queryFn: async () => (await fetchUnreadCount(viewerZone())).count,
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });

  const { data, isLoading } = useQuery({
    queryKey: ["notifications", "list"],
    queryFn: () => fetchNotifications(200, viewerZone()),
    enabled: open,
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ["notifications"] });

  const markReadMutation = useMutation({
    mutationFn: (id) => markNotificationRead(id),
    onSuccess: invalidate,
  });
  const markAllReadMutation = useMutation({
    mutationFn: () => markAllNotificationsRead(),
    onSuccess: invalidate,
  });

  const items = useMemo(() => data?.items || [], [data]);
  // Unread (any day) pinned on top, today's read items flat, and each of the
  // previous 6 days collapsed into its own group — see utils/notificationGroups.
  const grouped = useMemo(
    () => groupNotifications(items, new Date(), viewerZone()),
    [items]
  );
  return {
    unreadCount,
    items,
    unread: grouped.unread,
    readToday: grouped.today,
    days: grouped.days,
    isLoading,
    markRead: markReadMutation.mutate,
    markAllRead: markAllReadMutation.mutate,
  };
}
